const FLAG_TEXT = {
	emailPlaceholder: "your@email.com",
	commentsPlaceholder: "Enter your message here..."
} as const;

const WRITE_TEXT = {
	headlineLabel: "Headline",
	reviewLabel: "Your review",
	reviewPlaceholder: "What did you cook with it? What stood out?",
	reviewHelper: "Aim for around 300 characters.",
	recommendLabel: "Would you recommend this to a friend?",
	recommendHelper: "(Optional)",
	recommendYes: "Yes",
	recommendNo: "No",
	mediaHeading: "Add photos or videos",
	mediaHelper:
		"You may add up to 10 photos or videos, 25MB max each, uploaded without watermarks.",
	captionLabel: "Image Description",
	nameLabel: "Display name",
	namePlaceholder: "First name",
	locationPlaceholder: "State",
	emailPlaceholder: "your@email.com",
	emailHelper: "Adds a 'Verified' badge. Your email is never shown publicly.",
	terms: "By submitting, you agree to the "
} as const;

function setPlaceholder(el: Element | null, value: string): void {
	if (el instanceof HTMLElement && el.getAttribute("placeholder") !== value) {
		el.setAttribute("placeholder", value);
	}
}

function setText(el: Element | null | undefined, value: string): void {
	if (el && el.textContent !== value) {
		el.textContent = value;
	}
}

export function decorateFlagForm(root: ParentNode): void {
	const form = root.querySelector("form.pr-flag-review");
	if (!form) return;
	setPlaceholder(
		form.querySelector("#pr-email-field"),
		FLAG_TEXT.emailPlaceholder
	);
	setPlaceholder(
		form.querySelector("#pr-flag-comments"),
		FLAG_TEXT.commentsPlaceholder
	);
}

function decorateWriteReviewField(form: Element): void {
	const label = form.querySelector(
		".pr-comments-form-group .pr-control-label span"
	);
	setText(label, WRITE_TEXT.reviewLabel);
	const textarea =
		form.querySelector<HTMLTextAreaElement>("#pr-comments-input");
	if (!textarea) return;
	setPlaceholder(textarea, WRITE_TEXT.reviewPlaceholder);

	const wrapper = textarea.parentElement;
	if (!wrapper || wrapper.querySelector("[data-pr-char-counter]")) return;
	const row = document.createElement("div");
	row.setAttribute("data-pr-char-counter", "");
	row.className = "pr-war-decoration-helper-row";
	const helper = document.createElement("span");
	helper.textContent = WRITE_TEXT.reviewHelper;
	const counter = document.createElement("span");
	const max = textarea.maxLength > 0 ? textarea.maxLength : 1000;
	const renderCount = (): void => {
		counter.textContent = `${textarea.value.length}/${max}`;
	};
	renderCount();
	textarea.addEventListener("input", renderCount);
	row.append(helper, counter);
	wrapper.append(row);
}

function decorateWriteRecommend(form: Element): void {
	const legendText = form.querySelector(
		"#pr-bottomline legend .pr-form-control-error-wrapper span"
	);
	setText(legendText, WRITE_TEXT.recommendLabel);
	setText(
		form.querySelector("#pr-bottomline legend .pr-helper-text"),
		WRITE_TEXT.recommendHelper
	);
	const options = form.querySelectorAll(
		"#pr-bottomline .pr-btn-group label.pr-btn"
	);
	setText(options[0], WRITE_TEXT.recommendYes);
	setText(options[1], WRITE_TEXT.recommendNo);
}

function decorateWriteMedia(form: Element): void {
	const heading = form.querySelector(
		".pr-media_image-form-group .pr-file-input-label"
	);
	if (heading && !heading.querySelector("[data-pr-media-helper]")) {
		heading.textContent = "";
		const title = document.createElement("span");
		title.className = "pr-war-decoration-media-title";
		title.textContent = WRITE_TEXT.mediaHeading;
		const helper = document.createElement("span");
		helper.setAttribute("data-pr-media-helper", "");
		helper.className = "pr-war-decoration-media-helper";
		helper.textContent = WRITE_TEXT.mediaHelper;
		heading.append(title, helper);
	}
	form.querySelectorAll(
		".pr-media-table .pr-control-label span:first-child"
	).forEach((span) => setText(span, WRITE_TEXT.captionLabel));
}

function decorateWriteIdentity(form: Element): void {
	setText(
		form.querySelector(".pr-name-form-group .pr-control-label span"),
		WRITE_TEXT.nameLabel
	);
	setPlaceholder(
		form.querySelector("#pr-name-input"),
		WRITE_TEXT.namePlaceholder
	);
	setPlaceholder(
		form.querySelector("#pr-location-input"),
		WRITE_TEXT.locationPlaceholder
	);
	setPlaceholder(
		form.querySelector("#pr-email_collection-input"),
		WRITE_TEXT.emailPlaceholder
	);

	const emailWrapper = form.querySelector(
		".pr-email_collection-form-group .pr-form-control-error-wrapper"
	);
	if (emailWrapper && !emailWrapper.querySelector("[data-pr-email-helper]")) {
		const helper = document.createElement("div");
		helper.setAttribute("data-pr-email-helper", "");
		helper.className = "pr-form-control-helpertext-bottom";
		helper.textContent = WRITE_TEXT.emailHelper;
		emailWrapper.append(helper);
	}
}

function decorateWriteTerms(form: Element): void {
	const subscript = form.querySelector(".pr-subscript");
	if (!subscript || subscript.hasAttribute("data-pr-terms")) return;
	const links = subscript.querySelectorAll("a");
	if (links.length < 2) return;
	subscript.setAttribute("data-pr-terms", "");
	const terms = links[0];
	const privacy = links[1];
	subscript.textContent = "";
	subscript.append(WRITE_TEXT.terms, terms, " and ", privacy, ".");
}

// Tab order follows the DOM, but the layout order comes from CSS `order`, so
// PR's markup order has to be brought in line or focus jumps around the form.
const WRITE_FIELD_ORDER = [
	".pr-rating-form-group",
	".pr-headline-form-group",
	".pr-comments-form-group",
	".pr-bottomline-form-group",
	".pr-media_image-form-group",
	".pr-media_videourl-form-group",
	".pr-name-form-group",
	".pr-location-form-group",
	".pr-email_collection-form-group"
] as const;

function reorderWriteFields(form: Element): void {
	const fields = form.querySelector("#pr-war-form");
	if (!fields) return;
	const ordered = WRITE_FIELD_ORDER.map((selector) =>
		fields.querySelector(`:scope > ${selector}`)
	).filter((el): el is Element => el !== null);

	const current = Array.from(fields.children).filter((el) =>
		ordered.includes(el)
	);
	// Re-appending unconditionally would retrigger the observer that calls this.
	if (current.every((el, index) => el === ordered[index])) return;
	ordered.forEach((el) => fields.append(el));
}

function reorderWriteFooter(form: Element): void {
	const footer = form.querySelector(".pr-footer");
	const submit = footer?.querySelector(".pr-btn-review");
	if (!footer || !submit || footer.firstElementChild === submit) return;
	footer.prepend(submit);
}

export function decorateWriteForm(root: ParentNode): void {
	const form = root.querySelector("form.pr-war");
	if (!form) return;
	reorderWriteFields(form);
	reorderWriteFooter(form);
	setText(
		form.querySelector(".pr-headline-form-group .pr-control-label span"),
		WRITE_TEXT.headlineLabel
	);
	decorateWriteReviewField(form);
	decorateWriteRecommend(form);
	decorateWriteMedia(form);
	decorateWriteIdentity(form);
	decorateWriteTerms(form);
}
